import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Download, RefreshCw, Search, Mail, LogOut, Eye, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import MemberDetailModal from '@/components/MemberDetailModal';
import EditMemberModal from '@/components/EditMemberModal';
import DeleteMemberDialog from '@/components/DeleteMemberDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [resendingId, setResendingId] = useState(null);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentAdmin(response.data);
      fetchMembers();
    } catch (error) {
      console.error('Auth error:', error);
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const fetchMembers = async (searchQuery = '') => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/members`, {
        params: { search: searchQuery, limit: 100 },
        headers: getAuthHeaders()
      });
      setMembers(response.data.members);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching members:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        toast.error('Failed to load members');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentAdmin) {
      fetchMembers();
    }
  }, [currentAdmin]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMembers(search);
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/export-csv`, {
        responseType: 'blob',
        headers: getAuthHeaders()
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cme_members_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported successfully!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  const handleResendEmail = async (memberId, email) => {
    setResendingId(memberId);
    try {
      await axios.post(`${API}/admin/resend-email/${memberId}`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(`Confirmation email resent to ${email}!`);
    } catch (error) {
      console.error('Error resending email:', error);
      toast.error('Failed to resend email');
    } finally {
      setResendingId(null);
    }
  };

  const handleViewDetails = async (memberId) => {
    try {
      const response = await axios.get(`${API}/admin/member/${memberId}`, {
        headers: getAuthHeaders()
      });
      setSelectedMember(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching member details:', error);
      toast.error('Failed to load member details');
    }
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setShowEditModal(true);
  };

  const handleDeleteMember = (member) => {
    setSelectedMember(member);
    setShowDeleteDialog(true);
  };

  const handleMemberUpdated = () => {
    fetchMembers(search);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border-t-4 border-cme-red">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_cme-join-modal/artifacts/wocpjpx8_CME2020-HORIZONTAL.jpg" 
                alt="CME Logo"
                className="h-12 w-auto"
              />
              <div>
                <h1 className="text-3xl font-serif font-bold text-black">
                  Admin Dashboard
                </h1>
                <p className="text-gray-600">
                  {currentAdmin && `Welcome, ${currentAdmin.full_name || currentAdmin.username}`}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleExportCSV}
                className="bg-cme-red hover:bg-red-700 text-white"
                data-testid="export-csv-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-gray-300"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-12 border-gray-300 focus:border-cme-red focus:ring-cme-red"
                data-testid="search-input"
              />
            </div>
            <Button 
              type="submit"
              className="bg-black hover:bg-gray-800 text-white"
              data-testid="search-button"
            >
              Search
            </Button>
            <Button
              type="button"
              onClick={() => {
                setSearch('');
                fetchMembers('');
              }}
              variant="outline"
              className="border-gray-300"
              data-testid="refresh-button"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </form>

          <p className="text-sm text-gray-600 mt-4">
            Total Members: <span className="font-semibold text-black">{total}</span>
          </p>
        </div>

        {/* Members Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-cme-red mb-2" />
              <p className="text-gray-600">Loading members...</p>
            </div>
          ) : members.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-600">No members found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="members-table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Signed At
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {members.map((member) => (
                    <tr 
                      key={member.id} 
                      className="hover:bg-gray-50 transition-colors"
                      data-testid={`member-row-${member.id}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">
                          {member.first_name} {member.last_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-gray-600">{member.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-gray-600">{member.phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          {formatDate(member.accepted_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleViewDetails(member.id)}
                            size="sm"
                            variant="outline"
                            className="text-blue-600 border-blue-600 hover:bg-blue-50"
                            title="View Details"
                            data-testid={`view-member-${member.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleEditMember(member)}
                            size="sm"
                            variant="outline"
                            className="text-amber-600 border-amber-600 hover:bg-amber-50"
                            title="Edit Member"
                            data-testid={`edit-member-${member.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleResendEmail(member.id, member.email)}
                            disabled={resendingId === member.id}
                            size="sm"
                            variant="outline"
                            className="text-cme-red border-cme-red hover:bg-red-50"
                            title="Resend Email"
                            data-testid={`resend-email-${member.id}`}
                          >
                            {resendingId === member.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Mail className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            onClick={() => handleDeleteMember(member)}
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-600 hover:bg-red-50"
                            title="Delete Member"
                            data-testid={`delete-member-${member.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <MemberDetailModal
        member={selectedMember}
        open={showDetailModal}
        onClose={() => setShowDetailModal(false)}
      />

      <EditMemberModal
        member={selectedMember}
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={handleMemberUpdated}
      />

      <DeleteMemberDialog
        member={selectedMember}
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onSuccess={handleMemberUpdated}
      />
    </div>
  );
};

export default AdminDashboard;
